<template>
  <div class="app-container">
    <!-- Loading state -->
    <div
      v-if="store.loading"
      class="loading-container"
    >
      <AppLoader />
    </div>

    <!-- Error state -->
    <div v-else-if="!startSettings">
      <ErrorModal />
    </div>

    <!-- Main content -->
    <template v-else>
      <AppHeader v-if="showHeader" />
      <router-view />
      <AppFooter />
    </template>
  </div>
</template>

<script setup>
import { computed, watchEffect, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useStore } from "@/store/getStartStore";
import { connectTypaySocket } from "@/api/wsConnect";

// Components
import AppHeader from "@/components/AppHeader.vue";
import AppLoader from "@/components/ui/AppLoader.vue";
import ErrorModal from "@/components/errorModal.vue";
import AppFooter from "@/components/AppFooter.vue";

// Constants
// Сторінки з власним заголовком і кнопкою «назад» загальний хедер не показують,
// інакше на екрані два заголовки поспіль.
const HIDDEN_HEADER_ROUTES = ["/basket", "/saved", "/terms"];
const THEME_COLORS = {
  graphite: "#4a4a4a",
  default: "#f2f2f2",
};

// Store & Route
const route = useRoute();
const store = useStore();
const { startSettings } = storeToRefs(store);

// Computed
const showHeader = computed(() => !HIDDEN_HEADER_ROUTES.includes(route.path));

// Socket connection
let socket = null;
let socketConnected = false;

// Utility functions
const updateMetaTag = (name, content) => {
  let metaTag = document.querySelector(`meta[name='${name}']`);
  if (!metaTag) {
    metaTag = document.createElement("meta");
    metaTag.setAttribute("name", name);
    document.head.appendChild(metaTag);
  }
  metaTag.setAttribute("content", content);
};

const updatePageTheme = (styleWeb) => {
  if (!styleWeb) return;

  const themeColor =
    styleWeb.name === "graphite" ? THEME_COLORS.graphite : THEME_COLORS.default;

  const updatedStyle = { ...styleWeb, content: themeColor };
  store.changeTheme(updatedStyle);
  updateMetaTag("theme-color", themeColor);
};

const updatePageTitle = (companyName) => {
  if (companyName) {
    document.title = `${companyName} - Меню`;
  }
};

const handleTPayMessage = (response) => {
  if (!response?.body) return;

  try {
    const data = JSON.parse(JSON.parse(response.body));

    if (data.type === "wasPaid") {
      const savedOrderId = localStorage.getItem("orderId");
      if (savedOrderId === String(data.orderId)) {
        store.removeOrder();
      }
    }
  } catch (error) {
    console.error("[TPayMessage Error]:", error);
  }
};

const initializeSocket = async (settings) => {
  if (socketConnected || !settings) return;

  const shouldConnect =
    settings.isActiveWaiter || settings.isActiveWithoutWaiter;

  if (shouldConnect) {
    socketConnected = true;

    try {
      socket = await connectTypaySocket(
        (msg) => {
          handleTPayMessage(msg);
          store.setSocketMessage(msg);
        },
        (is, status) => {
          store.setSocketError({ is, status });
        },
        settings
      );
    } catch (error) {
      console.error("[Socket Connection Error]:", error);
      socketConnected = false;
    }
  }
};

// Watchers
watchEffect(() => {
  if (startSettings.value) {
    updatePageTheme(startSettings.value.styleWeB);
    updatePageTitle(startSettings.value.companyName);
    initializeSocket(startSettings.value);
  }
});

// Cleanup
onUnmounted(() => {
  if (socket) {
    // Додайте метод для закриття socket з'єднання якщо він є
    socket.close?.();
  }
});
</script>

<style lang="scss">
@import "@/assets/main.css";
@import "@/assets/scss/base/_modern-normalize";
@import "@/assets/scss/base/_default-settings";

.app-container {
  @apply flex flex-col min-h-[100dvh] pb-10 w-full max-w-[550px] mx-auto relative;
}

.loading-container {
  @apply flex justify-center items-center w-full h-screen;
}
</style>
